import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, TrendingUp, Sparkles, Target, LineChart, Layers, BarChart3, Cpu, Search, Users, RefreshCcw, Wand2, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "../hooks/useSEO";
import { useJsonLd, personSchema, websiteSchema, professionalServiceSchema, breadcrumb } from "../hooks/useJsonLd";

const stagger = { animate: { transition: { staggerChildren: 0.08 } } };
const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } } };

const specializations = ["AI Marketing", "Performance Marketing", "Growth Strategy", "CRO", "GTM & Analytics"];
const platforms = ["Google", "Meta", "Amazon DSP", "Native", "OTT", "Marketplace", "AI"];

const stats = [
  { v: "7+", l: "Years Experience" },
  { v: "50+", l: "Brands Scaled" },
  { v: "5-7x", l: "Avg ROAS" },
];

const heroChips = ["AI Marketing Agents", "IPL · SA20 · BBL", "OTT", "D2C"];

// Client / worked-with roster (styled wordmark pills — brand-safe)
const clients = [
  { name: "Paytm", bg: "#00B9F1", fg: "#FFFFFF", weight: 700, tracking: "-0.02em" },
  { name: "Kama Ayurveda", bg: "#0F1115", fg: "#D4B36A", weight: 500, tracking: "0.05em", italic: true },
  { name: "IPL", bg: "#1A237E", fg: "#F5B301", weight: 800, tracking: "0.02em" },
  { name: "SA20", bg: "#0EA5E9", fg: "#FFFFFF", weight: 800, tracking: "0.02em" },
  { name: "BBL", bg: "#111827", fg: "#F97316", weight: 800, tracking: "0.02em" },
  { name: "Above40", bg: "#0B3B1F", fg: "#F5B301", weight: 600, tracking: "-0.02em" },
  { name: "Styx Sports", bg: "#DC2626", fg: "#FFFFFF", weight: 700, tracking: "-0.02em" },
  { name: "CRIX", bg: "#0F172A", fg: "#3B82F6", weight: 800, tracking: "0.02em" },
  { name: "Sandbrix", bg: "#0F172A", fg: "#22D3EE", weight: 700, tracking: "-0.02em" },
];

const approachCards = [
  { icon: TrendingUp, label: "Traffic", copy: "Paid + Organic acquisition" },
  { icon: Target, label: "Tracking", copy: "GA4 · GTM · AppsFlyer" },
  { icon: Layers, label: "Funnel", copy: "Awareness → Purchase" },
  { icon: Wand2, label: "Landing Page", copy: "CRO-optimised" },
  { icon: LineChart, label: "Conversion", copy: "A/B, offer, copy" },
  { icon: RefreshCcw, label: "Retention", copy: "LTV & repeat" },
  { icon: Cpu, label: "Automation", copy: "AI ops & reports" },
];

const framework = [
  { step: "01", label: "Business Goals", note: "Anchor to revenue, not vanity" },
  { step: "02", label: "Customer Research", note: "ICP · JTBD · voice-of-customer" },
  { step: "03", label: "Traffic Acquisition", note: "Paid, Native, Organic" },
  { step: "04", label: "Conversion", note: "LP + funnel + offer" },
  { step: "05", label: "Retention", note: "Repeat, referral, LTV" },
  { step: "06", label: "Automation", note: "AI agents + reporting" },
  { step: "07", label: "Scale", note: "Compound the winners" },
];

const expertise = [
  { icon: Cpu, title: "AI Marketing Agents", tags: ["OpenAI", "Autonomous Ops", "Reporting"], accent: "#3B82F6" },
  { icon: TrendingUp, title: "Performance Marketing", tags: ["Google Ads", "Meta Ads", "DV360", "Amazon DSP"] },
  { icon: Target, title: "CRO", tags: ["Landing Pages", "Funnels", "A/B Testing"] },
  { icon: BarChart3, title: "Analytics", tags: ["GA4", "GTM", "AppsFlyer", "Looker Studio"] },
  { icon: Search, title: "Native Advertising", tags: ["Taboola", "Outbrain", "MGID"] },
];

export default function Home() {
  useSEO({
    title: "Shivani Ranawat · AI Growth Marketing Consultant | Performance, CRO & Automation",
    description: "AI Growth Marketing Consultant. I help startups and digital businesses grow faster using AI automation, performance marketing, CRO and data-driven growth systems. 7+ years, 50+ brands, 5–7x ROAS.",
    path: "/",
  });
  useJsonLd([personSchema, websiteSchema, professionalServiceSchema, breadcrumb([{ name: "Home", path: "/" }])]);
  return (
    <div data-testid="home-page">
      {/* ============ HERO ============ */}
      <section data-testid="hero-section" className="relative min-h-[100vh] mesh-bg grain overflow-hidden text-white pt-32 pb-16 md:pb-24">
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <motion.div variants={stagger} initial="initial" animate="animate" className="flex items-center justify-between text-[10px] sm:text-xs font-mono text-white/50 mb-10 md:mb-16">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] pulse-dot" />
              AVAILABLE Q1 2026 · SELECT ENGAGEMENTS
            </motion.div>
            <motion.div variants={fadeUp} className="hidden md:block">DECK / v2026.01</motion.div>
          </motion.div>

          <motion.div variants={fadeUp} initial="initial" animate="animate" className="max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#3B82F6]/30 bg-[#3B82F6]/10 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.2em] text-[#93C5FD]">
              <Sparkles size={11} /> Positioning · Rare Niche
            </div>
            <h1
              data-testid="hero-headline"
              className="mt-5 font-display text-[40px] sm:text-[64px] lg:text-[88px] leading-[1.02] sm:leading-[0.95] tracking-[-0.03em] text-white"
            >
              I build marketing systems{" "}
              <span className="text-white/50">that drive</span> revenue
              <span className="text-[#3B82F6]">.</span>{" "}
              <span className="text-white/50">Not just traffic.</span>
            </h1>
            <p className="mt-6 text-white/70 text-base sm:text-lg max-w-2xl leading-relaxed">
              I help startups and digital businesses grow faster using{" "}
              <span className="text-white">AI automation, performance marketing, CRO</span> and data-driven growth systems.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs sm:text-sm text-white/60 font-mono">
              {heroChips.map((c, i) => (
                <span key={c} className="inline-flex items-center gap-3">
                  {i > 0 && <span className="text-white/20">·</span>}
                  <span>{c}</span>
                </span>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link
                to="/work"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 rounded-full bg-white text-neutral-900 px-5 py-3 text-sm font-medium hover:bg-white/90 transition"
              >
                View Case Studies <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 text-white px-5 py-3 text-sm font-medium hover:border-white/60 transition"
              >
                Book a Strategy Call
              </Link>
            </div>
          </motion.div>

          {/* Identity + KPI dashboard */}
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <motion.div variants={fadeUp} initial="initial" animate="animate" className="lg:col-span-5">
              <div className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight">
                SHIVANI<br />RANAWAT
              </div>
              <div className="mt-4 text-xs font-mono text-white/50 tracking-[0.15em] uppercase">
                AI Growth Marketing Consultant
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {specializations.map((s) => (
                  <span
                    key={s}
                    data-testid={`spec-chip-${s.replace(/\s/g, "-").toLowerCase()}`}
                    className="text-xs font-medium px-3 py-1.5 rounded-full border border-white/15 bg-white/[0.03] text-white/80"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div variants={fadeUp} initial="initial" animate="animate" className="lg:col-span-7">
              <div className="glass-dark rounded-2xl p-5 hover-glow">
                <div className="flex items-center justify-between text-xs font-mono text-white/50 border-b border-white/5 pb-3 mb-5">
                  <span className="inline-flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#10B981]" /> KPI · SNAPSHOT
                  </span>
                  <span>LIVE</span>
                </div>

                <div className="grid grid-cols-3 gap-3 sm:gap-4">
                  {stats.map((k) => (
                    <div key={k.l} data-testid={`kpi-${k.l.replace(/\s/g, "-").toLowerCase()}`} className="rounded-xl bg-white/[0.02] border border-white/5 p-3 sm:p-4">
                      <div className="font-display text-2xl sm:text-4xl text-white">{k.v}</div>
                      <div className="mt-2 text-[10px] sm:text-[11px] uppercase tracking-[0.15em] text-white/40">{k.l}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 rounded-xl bg-white/[0.02] border border-white/5 p-4">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-3">Platforms Operated</div>
                  <div className="flex flex-wrap gap-2">
                    {platforms.map((p, i) => (
                      <span
                        key={p}
                        className={`text-[11px] font-mono px-2.5 py-1 rounded-md border ${i % 2 ? "border-[#3B82F6]/30 bg-[#3B82F6]/10 text-[#93C5FD]" : "border-white/10 bg-white/[0.02] text-white/70"}`}
                      >
                        {p.toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-5 rounded-xl bg-white/[0.02] border border-white/5 p-4">
                  <div className="flex items-center justify-between text-[11px] font-mono text-white/40 mb-2">
                    <span>ROAS · TRAILING 90D</span>
                    <span className="text-[#10B981]">+284%</span>
                  </div>
                  <svg viewBox="0 0 400 90" className="w-full h-24">
                    <defs>
                      <linearGradient id="g" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path d="M0,70 L40,60 L80,65 L120,50 L160,45 L200,38 L240,42 L280,28 L320,22 L360,18 L400,10 L400,90 L0,90 Z" fill="url(#g)" />
                    <path d="M0,70 L40,60 L80,65 L120,50 L160,45 L200,38 L240,42 L280,28 L320,22 L360,18 L400,10" fill="none" stroke="#3B82F6" strokeWidth="1.5" />
                  </svg>
                </div>
              </div>

              <div className="mt-4 text-[11px] font-mono text-white/30 text-right">
                * Aggregate across 50+ brands · Budgets $4K → $125K+ per campaign
              </div>
            </motion.div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/40 text-xs font-mono flex items-center gap-2">
          <span>SCROLL</span><ArrowDown size={12} />
        </div>
      </section>

      {/* ============ CLIENT / WORKED WITH STRIP ============ */}
      <section data-testid="clients-strip" className="bg-[#0F1115] text-white border-t border-white/5 py-14 md:py-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex items-center justify-between mb-8">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/50">
              Selected Engagements
            </div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/30">
              9 of 50+
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {clients.map((c) => (
              <div
                key={c.name}
                data-testid={`client-${c.name.replace(/\s/g, "-").toLowerCase()}`}
                className="rounded-xl border border-white/10 h-16 flex items-center justify-center transition hover:border-white/30"
                style={{ background: c.bg }}
              >
                <span
                  className="font-display text-sm md:text-base"
                  style={{
                    color: c.fg,
                    fontWeight: c.weight,
                    letterSpacing: c.tracking,
                    fontStyle: c.italic ? "italic" : "normal",
                  }}
                >
                  {c.name}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-6 text-[11px] font-mono text-white/30">
            * Verticals: OTT · D2C · Fintech · Beauty · Sports · Cricket Tournaments · Marketplaces
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section data-testid="testimonials-section" className="bg-[#0F1115] text-white border-t border-white/5 py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-4">
              <div className="text-xs font-mono tracking-[0.2em] uppercase text-white/40 mb-3">Voices from the room</div>
              <h2 className="font-display text-3xl md:text-5xl leading-[1.05] tracking-tight">
                What founders,<br /><span className="text-white/50">CMOs and product leaders</span> say.
              </h2>
            </div>
            <div className="lg:col-span-8 text-white/60 text-sm md:text-base leading-relaxed lg:pt-4">
              Direct quotes from selected engagements. Full case studies available on request under NDA.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                quote: "Shivani rebuilt our paid acquisition from scratch during peak IPL. Within 6 weeks our ROAS crossed 6× on a doubled budget — and CAC actually fell.",
                who: "Head of Growth",
                where: "Cricket · OTT",
                accent: "#3B82F6",
              },
              {
                quote: "She's the rare marketer who lives in the numbers and the creative brief at the same time. The AI reporting agent she built saves us 12+ hours a week.",
                who: "Founder & CEO",
                where: "D2C · Beauty",
                accent: "#10B981",
              },
              {
                quote: "We plugged Shivani into a stuck GTM. In 30 days we had a working funnel, live A/B testing on our LP, and a Monday-morning revenue report I actually read.",
                who: "CMO",
                where: "B2B SaaS",
                accent: "#F59E0B",
              },
            ].map((t, i) => (
              <div key={i} data-testid={`testimonial-${i + 1}`} className="glass-dark rounded-2xl p-6 md:p-8 hover-glow">
                <svg width="32" height="24" viewBox="0 0 32 24" fill="none" className="mb-6">
                  <path d="M0 24V13.5C0 8.83 1.08 5.42 3.24 3.25C5.4 1.08 8.4 0 12.24 0V6.06C10.32 6.06 8.88 6.6 7.92 7.68C6.96 8.76 6.48 10.26 6.48 12.18H12.24V24H0ZM19.76 24V13.5C19.76 8.83 20.84 5.42 23 3.25C25.16 1.08 28.16 0 32 0V6.06C30.08 6.06 28.64 6.6 27.68 7.68C26.72 8.76 26.24 10.26 26.24 12.18H32V24H19.76Z" fill={t.accent} fillOpacity="0.5" />
                </svg>
                <p className="text-base md:text-lg text-white/85 leading-relaxed">{t.quote}</p>
                <div className="mt-6 pt-5 border-t border-white/5 flex items-center justify-between">
                  <div>
                    <div className="text-sm text-white font-display">{t.who}</div>
                    <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 mt-1">{t.where}</div>
                  </div>
                  <div className="text-[10px] font-mono text-white/30">Verified · NDA</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3 text-center">
            {[
              { v: "9/10", l: "Clients renew for a 2nd engagement" },
              { v: "24h", l: "Median first response time" },
              { v: "$0 → $125K+", l: "Monthly ad budgets managed" },
            ].map((k) => (
              <div key={k.l} className="rounded-xl border border-white/5 bg-white/[0.02] p-5">
                <div className="font-display text-2xl md:text-3xl text-white">{k.v}</div>
                <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 mt-1">{k.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ APPROACH ============ */}
      <section data-testid="approach-section" className="bg-[#F7F7F5] py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-3 text-xs font-mono tracking-[0.2em] uppercase text-neutral-500">
              02 — Diagnosis
            </div>
            <div className="lg:col-span-9">
              <h2 className="font-display text-3xl sm:text-4xl md:text-6xl leading-[1.02] tracking-tight text-neutral-900">
                Every business loses revenue somewhere.<br />
                <span className="text-neutral-400">I help identify and fix</span> growth bottlenecks.
              </h2>
            </div>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 -translate-y-1/2 dashed-line" />
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 relative">
              {approachCards.map((c, i) => {
                const Icon = c.icon;
                return (
                  <div
                    key={c.label}
                    data-testid={`approach-card-${c.label.replace(/\s/g, "-").toLowerCase()}`}
                    className="group relative bg-white rounded-2xl p-5 border border-neutral-200 hover:border-[#3B82F6]/40 hover:shadow-[0_10px_40px_-15px_rgba(59,130,246,0.35)] transition"
                  >
                    <div className="text-[10px] font-mono text-neutral-400">STEP · {String(i + 1).padStart(2, "0")}</div>
                    <div className="mt-6 w-10 h-10 rounded-lg bg-[#3B82F6]/10 text-[#3B82F6] flex items-center justify-center">
                      <Icon size={18} />
                    </div>
                    <div className="mt-4 font-display text-lg text-neutral-900">{c.label}</div>
                    <div className="text-xs text-neutral-500 mt-1">{c.copy}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============ FRAMEWORK ============ */}
      <section data-testid="framework-section" className="bg-white py-24 md:py-36 border-t border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-3 text-xs font-mono tracking-[0.2em] uppercase text-neutral-500">
              03 — Framework
            </div>
            <div className="lg:col-span-9">
              <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#3B82F6] mb-4">
                <Sparkles size={12} /> Proprietary Framework
              </div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-6xl leading-[1.02] tracking-tight text-neutral-900">
                Revenue Growth Engine<span className="text-[#3B82F6]">™</span>
              </h2>
              <p className="mt-4 text-neutral-500 max-w-2xl">
                A seven-stage operating system for compounding revenue. Deployed across OTT, D2C, B2B and marketplace businesses.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3">
            {framework.map((f, i) => (
              <div key={f.label} className="relative">
                <div
                  data-testid={`framework-step-${i + 1}`}
                  className="rounded-2xl border border-neutral-200 bg-neutral-50 p-5 h-full hover:border-[#3B82F6] hover:bg-white transition"
                >
                  <div className="font-mono text-[10px] text-neutral-400">{f.step}</div>
                  <div className="mt-8 font-display text-xl text-neutral-900 leading-tight">{f.label}</div>
                  <div className="mt-2 text-xs text-neutral-500">{f.note}</div>
                </div>
                {i < framework.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-2.5 -translate-y-1/2 z-10 bg-white border border-neutral-200 rounded-full w-5 h-5 items-center justify-center text-neutral-400">
                    <ArrowRight size={10} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ EXPERTISE ============ */}
      <section data-testid="expertise-section" className="bg-[#EEEDE8] py-24 md:py-36 border-t border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-3 text-xs font-mono tracking-[0.2em] uppercase text-neutral-500">
              04 — Expertise
            </div>
            <div className="lg:col-span-9 flex items-end justify-between gap-6 flex-wrap">
              <h2 className="font-display text-3xl sm:text-4xl md:text-6xl leading-[1.02] tracking-tight text-neutral-900">
                Core expertise.
              </h2>
              <Link
                to="/services"
                data-testid="expertise-services-link"
                className="inline-flex items-center gap-2 text-sm font-medium text-[#3B82F6] hover:text-[#2563EB]"
              >
                See all services <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {expertise.map((e, i) => {
              const Icon = e.icon;
              const featured = i === 0;
              return (
                <div
                  key={e.title}
                  data-testid={`expertise-${e.title.replace(/\s/g, "-").toLowerCase()}`}
                  className={`rounded-2xl p-7 border transition group ${
                    featured
                      ? "bg-[#0F1115] border-white/10 text-white hover:border-[#3B82F6]"
                      : "bg-white border-neutral-200 hover:border-[#3B82F6]"
                  }`}
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${featured ? "bg-[#3B82F6]/20 text-[#3B82F6]" : "bg-[#3B82F6]/10 text-[#3B82F6]"}`}>
                    <Icon size={20} />
                  </div>
                  <div className={`mt-8 font-display text-2xl ${featured ? "text-white" : "text-neutral-900"}`}>{e.title}</div>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {e.tags.map((t) => (
                      <span key={t} className={`text-[11px] font-mono px-2.5 py-1 rounded-md border ${featured ? "border-white/15 bg-white/5 text-white/70" : "border-neutral-200 bg-neutral-50 text-neutral-600"}`}>
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-white border border-neutral-200 p-8">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500">Ready to compound?</div>
              <div className="mt-2 font-display text-2xl text-neutral-900">Let&apos;s find your revenue leak.</div>
              <Link
                to="/contact"
                data-testid="expertise-cta"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0F1115] text-white px-5 py-3 text-sm font-medium hover:bg-neutral-800"
              >
                Book a 30-min audit call <ArrowRight size={14} />
              </Link>
            </div>
            <div className="rounded-2xl bg-[#0F1115] text-white border border-white/10 p-8">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/40 inline-flex items-center gap-2">
                <Zap size={12} className="text-[#3B82F6]" /> Signature Build
              </div>
              <div className="mt-2 font-display text-2xl">Autonomous AI Marketing Agent.</div>
              <div className="mt-1 text-sm text-white/60">Ingests ads + analytics → detects issues → recommends budget → emails a decision-ready report.</div>
              <Link
                to="/work#ai-agent"
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/20 text-white px-5 py-3 text-sm font-medium hover:border-white/60"
              >
                See the build <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
